import { Exception } from '@src/types';

import _ from 'lodash';

/**
 * Usage:
 * // This code snippet will fire checkpoints based on the success value of a promise.
 * let timeline = new Timeline("HelpingACustomer");
 * loadCustomers().then(function() {
 *  timeline.checkpoint("LoadedCustomer");
 * }, function() {
 *  timeline.checkpoint("LoadedCustomer.Error");
 * });
 *
 * // This code snippet can be used to record latency for a given checkpoint. Assumes above code is running.
 * let latencyMetric = new LatencyMetric();
 * timeline.whenMultipleCheckpointsHaveFired(['LoadedCustomer', 'LoadedCustomerFacets'],
 * ['LoadedCustomer.Error', 'LoadedCustomer.Aborted'])
 * .then(function() {
 *  latencyMetric.stop();
 *  // Fired if an only if LoadedCustomer AND 'LoadedCustomerFacets have fired and LoadedCustomer.
 *  // Error OR LoadedCustomer.Aborted has not fired.
 * }, function() {
 *  recordCounterForError();
 *  // Fired if an only if LoadedCustomer.Error OR LoadedCustomer.Aborted fired before LoadedCustomer
 * });
 *
 * // This code snippet can be used to record latency for a given checkpoint after another checkpoint has fired.
 * timeline.whenCheckpointHasFired('CustomerListLoaded.Start').then(function() {
 *
 *   let latencyMetric = new LatencyMetric();
 *   latencyMetric.start(); // Fired if an only if LoadedCustomer has fired and LoadedCustomer.Error has not fired.
 *   timeline.whenMultipleCheckpointsHaveFired(['LoadedCustomer'], ['LoadedCustomer.Error']).then(function() {
 *      latencyMetric.stop();
 *   });
 * });
 *
 * NOTE: Use checkpoint with async/await or .then()
 */
export class Timeline {
    constructor(id) {
        if (!this.isValidString(id)) {
            throw new Error('Timeline id must be constructed with a valid string.');
        }
        this.timelineId = id;
        this.eventCallback = {};
    }

    private timelineId;
    private eventCallback: { [key: string]: (args?) => any };

    private isValidString(str) {
        return str != null && _.isString(str) && str.length > 0;
    }

    private createMultipleEventListener(successEventTypes, failureEventTypes) {
        if (successEventTypes == null || !_.isArray(successEventTypes)) {
            throw new Exception({ message: 'SuccessEventTypes must be an array of strings.' });
        }
        if (successEventTypes.length <= 0) {
            throw new Exception({ message: 'Success event types must contain at least 1 event.' });
        }
        failureEventTypes = failureEventTypes || [];
        if (!_.isArray(failureEventTypes)) {
            throw new Exception({ message: 'FailureEventTypes must be an array of strings.' });
        }
        const allEventTypes = successEventTypes.concat(failureEventTypes);
        allEventTypes.forEach((eventType) => {
            if (!this.isValidString(eventType)) {
                throw new Exception({ message: 'Event types must be strings.' });
            }
        });

        const receivedEventTypes = {};
        const eventDeregistration = [];
        let deferred;
        const promise = new Promise((resolve, reject) => {
            deferred = { promise, resolve, reject };
        });
        deferred.promise = promise;
        const destroy = () => {
            console.debug('Destroying: ', eventDeregistration);
            eventDeregistration.forEach((receivedEventType) => {
                if (this.eventCallback.hasOwnProperty(receivedEventType)) {
                    delete this.eventCallback[receivedEventType];
                }
            });
        };
        allEventTypes.forEach((eventType) => {
            receivedEventTypes[eventType] = false;
        });

        const watchCheckpoints = (newReceivedEventTypes) => {
            let failureReceived = false;
            failureEventTypes.forEach((eventType) => {
                failureReceived = failureReceived || newReceivedEventTypes[eventType];
            });

            let allFired = true;
            successEventTypes.forEach(function (eventType) {
                allFired = allFired && newReceivedEventTypes[eventType];
            });

            if (failureReceived) {
                console.info('One or more failure events have been checkpointed.');
                deferred.reject();
            }
            if (allFired) {
                console.debug('All event types have been fired.');
                deferred.resolve();
            }
            if (allFired || failureReceived) {
                destroy();
            }
        };
        allEventTypes.forEach((eventType) => {
            this.eventCallback[eventType] = () => {
                console.debug('Listening to broadcast = ' + eventType);
                receivedEventTypes[eventType] = true;
                watchCheckpoints(receivedEventTypes);
            };
            console.warn(this.eventCallback[eventType]);
            eventDeregistration.push(eventType);
        });
        return {
            destroy,
            promise: deferred.promise,
        };
    }

    public listeners = [];

    /**
     * @param: (String) (required) the checkpoint name.
     * @return: undefined
     */
    public async checkpoint(eventType: string) {
        if (!this.isValidString(eventType)) {
            return Promise.reject('Checkpoint event name must be a valid string.');
        }
        if (this.eventCallback[eventType]) {
            this.eventCallback[eventType]();
        }
    }

    /**
     * @param: (String) (required) the checkpoint name
     * @callback: (required) a callback that takes no arguments to be called when the checkpoint has been fired
     * @return: (function) deregistration function
     *
     * Users can deregister if they wish.
     */
    public onCheckpoint(eventType: string, callback) {
        if (!this.isValidString(eventType)) {
            throw new Error('Checkpoint event name must be a valid string.');
        }
        this.eventCallback[eventType] = callback;
        const deregister = () => {
            delete this.eventCallback[eventType];
        };
        return deregister;
    }

    /**
     * @param: (Array of String) (required) the checkpoints that must fire
     *         before the promise resolves.
     * @param: (Array of String) the checkpoints, which if they fire, will
     *         prevent the above promise from resolving.
     * @return: (promise) resolves when the checkpoint fires, rejects if any of the failure ones fire.
     */
    public whenMultipleCheckpointsHaveFired(requiredCheckpoints, failureCheckpoints) {
        const listener = this.createMultipleEventListener(requiredCheckpoints, failureCheckpoints);
        this.listeners.push(listener);
        return listener.promise;
    }

    /**
     * @param: (String) (required) the checkpoint name that must be fired before the returned
     *         promise is resolved.
     * @return: (promise) resolves when the checkpoint fires, never rejects.
     */
    public whenCheckpointHasFired(eventName) {
        return this.whenMultipleCheckpointsHaveFired([eventName], []);
    }

    /**
     * Destroys all the existing listeners on the timeline.
     * @return: undefined
     */
    public ignorePendingListeners() {
        this.listeners.forEach(function (listener) {
            listener.destroy();
        });
    }
    public toString() {
        return this.timelineId;
    }
}
